import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList, ResponsiveContainer,
} from "recharts";
import "../DashboardStyles.css";
import PanhandleMap from "./PanhandleMap";
import { Figure23a, Figure23b, Figure23c } from "./IncomeFigures";

// 2020 Decennial Census (April 1, 2020)
const POPULATION = [
  { county: "Scotts Bluff", pop: 36084 },
  { county: "Box Butte",    pop: 10842 },
  { county: "Cheyenne",     pop: 9468 },
  { county: "Dawes",        pop: 8199 },
  { county: "Sheridan",     pop: 5127 },
  { county: "Morrill",      pop: 4555 },
  { county: "Kimball",      pop: 3434 },
  { county: "Garden",       pop: 1874 },
  { county: "Deuel",        pop: 1838 },
  { county: "Sioux",        pop: 1135 },
  { county: "Banner",       pop: 674 },
];

const COMPARISON = [
  ["Total Population", "1,961,504", "83,230 (4.2%)", "~1 in 24 Nebraskans, across a vast geographic area"],
  ["Median Household Income", "$76,376", "$62,600*", "Panhandle households earn nearly $14,000 less per year"],
  ["Bachelor’s Degree or Higher", "35.4%", "24.9%*", "Attainment lags the state by more than 10 points"],
  ["Employment Rate", "65.9%", "60.7%*", "Reflects rural labor-market constraints and age structure"],
  ["Without Health Care Coverage", "7.1%", "9.5%*", "Uninsured rate exceeds state average"],
  ["Total Employer Establishments", "56,503", "2,348 (4.2%)", "Business density is low relative to land area"],
  ["Hispanic or Latino", "12.7%", "15.5%", "Share exceeds the state average"],
];

function PopulationChart() {
  return (
    <figure className="ceds-figure">
      <p className="fig-title">Nebraska Panhandle: Population by County (2020 Census)</p>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={POPULATION} margin={{ top: 20, right: 12, left: 8, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E2D6" />
          <XAxis dataKey="county" angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={(v) => v.toLocaleString()} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => [v.toLocaleString(), "Residents"]} />
          <Bar dataKey="pop" isAnimationActive={false}>
            {POPULATION.map((d) => (
              <Cell key={d.county} fill={d.county === "Scotts Bluff" ? "#1B2A4A" : "#7A8B6F"} />
            ))}
            <LabelList dataKey="pop" position="top" formatter={(v) => v.toLocaleString()} style={{ fontSize: 11 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <figcaption>Source: U.S. Census Bureau, 2020 Decennial Census (April 1, 2020).</figcaption>
    </figure>
  );
}

export default function Chapter2Dashboard() {
  return (
    <div className="chapter chapter2">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 2</p>
        <h2>Population and Demographics</h2>

        <h3>2.1 Introduction</h3>
        <p>
          The Nebraska Panhandle is a geographically expansive region in the far
          western portion of the state, encompassing eleven counties: Banner,
          Box Butte, Cheyenne, Dawes, Deuel, Garden, Kimball, Morrill, Scotts
          Bluff, Sheridan, and Sioux. Together, these counties cover a
          substantial share of Nebraska’s land area while accounting for
          approximately 4.2 percent of the state’s total population.
          Understanding the demographic composition of the region is essential
          to identifying workforce development needs, infrastructure priorities,
          housing challenges, and economic development opportunities addressed
          throughout this CEDS.
        </p>
        <p>
          Population figures in this chapter are drawn from the U.S. Census
          Bureau’s 2020 Decennial Census, which provides a complete count rather
          than a sample-based estimate and is therefore the most reliable
          population source available at the county level, particularly for the
          region’s smallest and most rural counties. Other demographic and
          economic indicators in this chapter, including income, educational
          attainment, employment, health coverage, and housing, are drawn from
          the American Community Survey (ACS) 5-Year Estimates, the appropriate
          source for characteristics the Decennial Census does not collect.
          Where regional figures are presented, population counts reflect direct
          sums across all eleven counties, and rates or averages reflect
          population-weighted calculations to account for the significant
          variation in county size across the region.
        </p>

        <h4>A Frontier Region by Scale</h4>
        <p>
          While home to just 4.2% of Nebraska’s population, the Panhandle
          encompasses approximately 18.5% of the state’s land area. In other
          words, the Nebraska Panhandle contains approximately one-fifth of
          Nebraska’s land area but only one-twenty-fourth of its population. At
          approximately 3.5 residents per square mile, the defining demographic
          characteristic is not simply population loss or rurality, but
          population dispersion. The region’s low-density settlement pattern
          increases the per-capita cost of broadband deployment, healthcare
          delivery, transportation infrastructure, workforce services, and
          emergency response while requiring communities to collaborate across
          vast distances.
        </p>
        <p>
          The region’s challenges (and opportunities) are therefore shaped as
          much by geographic scale as by population size. Long travel distances,
          dispersed settlement patterns, and higher infrastructure costs
          influence nearly every topic addressed in this CEDS, from housing and
          workforce development to healthcare, broadband, and transportation.
        </p>

        <PanhandleMap />

        <h3>2.2 Population Overview</h3>
        <p>
          The Nebraska Panhandle has a combined population of 83,230 residents.
          Scotts Bluff County is by far the most populous county in the region,
          home to approximately 36,084 residents, representing 43 percent of the
          Panhandle’s total population. Box Butte (10,842) and Cheyenne (9,468)
          counties are the next most populated, followed by Dawes (8,199),
          Sheridan (5,127), and Morrill (4,555). The remaining five counties
          (Kimball, Garden, Deuel, Sioux, and Banner) each have populations
          below 3,500, with Banner County recording the smallest population in
          the region at just 674 residents.
        </p>
        <p>
          This extreme concentration of population in Scotts Bluff County has
          important implications for regional planning. Services,
          infrastructure, and economic activity are heavily centered in the
          Scotts Bluff/Gering metropolitan area, while the outlying counties
          face the challenges common to frontier and remote rural communities:
          long distances to services, limited local employment options, and
          ongoing population decline driven by outmigration of working-age
          residents.
        </p>

        <PopulationChart />

        <h3>2.3 Income and Economic Well-Being</h3>
        <p>
          The population-weighted median household income for the Nebraska
          Panhandle is approximately $62,600, compared to the Nebraska statewide
          median of $76,376 — a gap of nearly $13,800. This disparity reflects
          the structural economic characteristics of the region, which is
          dominated by agriculture, natural resources, retail trade, and health
          care rather than the higher-wage professional and technology sectors
          concentrated in the Omaha and Lincoln metropolitan areas.
        </p>
        <p>
          Household income varies considerably across the region’s eleven
          counties. Banner County records the highest median household income at
          $78,839 (ACS 5-year, 2020–2024). This figure carries a margin of error
          of ±$13,607 — about 17 percent of the estimate itself — and swung 14
          percent year-over-year in the prior ACS release; as with other
          small-county figures in this CEDS, it should be read as directionally
          informative rather than precisely descriptive. See Appendix D for a
          full discussion of small-county data limitations. Garden County
          records the lowest at $41,882 — more than $34,000 below the state
          median, reflecting deep rural poverty and limited economic
          opportunity. Across most Panhandle counties, median household incomes
          fall in the $53,000–$70,000 range.
        </p>
        <p>
          Per capita personal income (a distinct measure from median household
          income, capturing total personal income divided by population rather
          than the midpoint of household earnings) tells a related but sharper
          story. In 2024, the Panhandle’s population-weighted per capita
          personal income was $60,921, compared to the Nebraska average of
          $72,701. This is a gap of $11,780, or 16.2 percent. The gap has widened
          over the period: it stood at 10.1 percent in 2020 and reached 17.2
          percent in 2022, driven by a year in which regional per capita income
          declined while the state’s rose. Regional figures throughout this
          section are population-weighted, calculated as total regional personal
          income divided by total regional population, consistent with the
          methodology stated in Section 2.1.
        </p>
        <p className="source-note">Source: U.S. Bureau of Economic Analysis, CAINC1, released 2025.</p>
        <p>
          County-level per capita income reveals a pattern distinct from the
          median household income figures above. In 2024, two Panhandle counties
          exceeded the Nebraska average: Sioux ($85,546) and Morrill ($79,808).
          Both are among the region’s least populated, and per capita income in
          counties of this size is highly volatile. Banner County exceeded the
          state average in 2023 at $80,999 and fell to $69,174 the following
          year. These figures reflect agricultural proprietor income and
          property income concentrated among few residents rather than
          broad-based wage growth, which is why they diverge from the median
          household income pattern. The region’s three most populous counties
          (Scotts Bluff, Box Butte, and Cheyenne) fall well below the state
          average, and Deuel and Dawes record the lowest per capita income in
          the region. This divergence between population-weighted regional
          disadvantage and county-level variation underscores a recurring theme
          of this CEDS: regional averages can mask meaningfully different
          conditions across the Panhandle’s eleven counties, and economic
          development strategy should account for both scales.
        </p>
        <p className="source-note">
          Source note: Figures are drawn from the U.S. Bureau of Economic
          Analysis, Regional Economic Accounts, Table CAINC1. County data reflect
          the February 2, 2026 revision; state data reflect the April 9, 2026
          revision. BEA revises these series annually, so current figures may
          differ from those published here.
        </p>

        <Figure23a />
        <Figure23b />

        <p>
          The Panhandle’s income gap is not simply a Panhandle-vs-Nebraska-average
          story; it holds against every comparable Nebraska development district
          individually. Pulling the same BEA per capita personal income data for
          Nebraska’s other five development districts: Central Nebraska (CNEDD),
          Southeast Nebraska (SENDD), Northeast Nebraska (NENEDD), South Central
          Nebraska (SCEDD), and West Central Nebraska (WCNDD), plus the
          Omaha-area Metropolitan Area Planning Agency (MAPA) region, shows the
          Panhandle finishing last among all seven regions in every year from
          2021 through 2024. In 2024, the Panhandle’s population-weighted per
          capita personal income of $60,921 trails West Central Nebraska, the
          next-lowest district, by $3,622, and trails South Central Nebraska (the
          highest-performing rural district) by $7,503. The gap to the
          Omaha-area MAPA region is substantially larger: $80,711, a difference
          of $19,790, or 32.5 percent above the Panhandle figure.
        </p>
        <p>
          This comparison sharpens, rather than changes, this CEDS’s central
          argument. The Panhandle is not an outlier among Nebraska’s rural
          development districts (West Central and Northeast Nebraska post
          broadly similar figures) but it is consistently at or near the bottom
          of that rural cohort, and the entire rural cohort trails the
          Omaha-area region by a wide and widening margin. This is the
          “bypassed, not outcompeted” dynamic this CEDS describes in Chapter 1:
          rural Nebraska generally, and the Panhandle specifically, is not
          losing ground to a single more dynamic peer region so much as it is
          collectively distant from where the state’s growth is concentrating.
        </p>

        <Figure23c />

        <h3>2.4 Educational Attainment</h3>
        <p>
          Educational attainment in the Nebraska Panhandle lags significantly
          behind state and national benchmarks. The population-weighted share of
          adults with a bachelor’s degree or higher across the region is 24.9
          percent, compared to 35.4 percent statewide. This is a gap of more
          than 10 percentage points. Dawes County (36.2%) stands out as the
          highest in the region, driven by the presence of Chadron State
          College. At the lower end, Kimball County (15.0%) and Box Butte County
          (18.6%) record the lowest bachelor’s degree attainment rates in the
          region.
        </p>

        <h3>2.5 Employment</h3>
        <p>
          The population-weighted employment rate across the Nebraska Panhandle
          is 60.7 percent, compared to 65.9 percent statewide. Employment rates
          across the region’s counties range from a low of 50.7 percent in
          Garden County to a high of 65.0 percent in Banner County. Low
          employment rates in frontier counties often reflect discouraged
          workers who have exited the labor force, retirees, and individuals
          engaged in subsistence agriculture or informal economic activity.
        </p>

        <h3>2.6 Housing</h3>
        <p>
          The Nebraska Panhandle contains a total of 38,630 housing units across
          its eleven counties, serving approximately 36,505 households. Scotts
          Bluff County dominates the regional housing stock with 16,480 units.
          Housing affordability and availability are recurring themes in
          Panhandle economic development discussions. While median home prices
          in the region are generally lower than statewide averages, the quality
          and age of the existing housing stock presents challenges for economic
          recruitment and community revitalization.
        </p>

        <h3>2.7 Health Care Coverage</h3>
        <p>
          The population-weighted share of residents without health care
          coverage is 9.5 percent, compared to 7.1 percent statewide. Kimball
          County records the highest uninsured rate at 14.4 percent, followed by
          Sheridan (10.9%), Scotts Bluff (10.2%), Dawes (9.6%), and Deuel
          (9.1%). The health coverage gap has direct economic development
          implications, creating a cycle of deferred care, poorer health
          outcomes, and reduced workforce productivity.
        </p>

        <h3>2.8 Business Establishments</h3>
        <p>
          The Nebraska Panhandle is home to 2,348 employer establishments,
          representing approximately 4.2 percent of Nebraska’s total. Scotts
          Bluff County accounts for 1,017 of these establishments (43.3%),
          reflecting its role as the regional economic hub. Frontier counties
          such as Banner (6), Sioux (16), and Garden (46) have very limited
          business presence.
        </p>

        <h3>2.9 Ethnic and Racial Composition</h3>
        <p>
          Hispanic or Latino residents make up 15.5 percent of the Panhandle’s
          population, compared with 12.7 percent statewide. Scotts Bluff County
          has both the largest Hispanic population in absolute terms, at 8,551
          residents, and the highest share in the region at 23.9 percent,
          followed by Morrill at 14.7 percent and Box Butte at 12.6 percent.
          Supporting the full economic inclusion and upward mobility of the
          region’s Hispanic community, including pathways to business ownership,
          access to higher education, and civic participation, is a priority
          reflected in this CEDS.
        </p>
        <p>
          This shift is concentrated in the region’s youngest residents. Across
          the eleven counties, 36.1 percent of children under age five are of a
          race or ethnicity other than White alone, non-Hispanic. In six
          counties the share exceeds one third: Scotts Bluff (49.2 percent),
          Kimball (46.5), Sheridan (36.7), Dawes (34.0), Deuel (33.9), and
          Morrill (33.7). This is a meaningfully higher share than among the
          region’s population overall. The practical implication for this CEDS
          is straightforward: the demographic composition of the Panhandle’s
          future workforce will differ from its current workforce, and Goal 2’s
          workforce-pipeline strategies (apprenticeships, employer partnerships,
          WNCC and Chadron State program alignment) are most effective at
          building regional labor supply if they reach the full population of
          Panhandle students, not a subset of it. This is a labor-supply
          planning consideration as much as a demographic one: a regional
          economy facing the workforce contraction documented in Chapter 5.4
          cannot afford a pipeline that underperforms for any segment of its own
          future labor force.
        </p>
        <p className="source-note">
          Source note: race and ethnicity distribution and under-five population
          composition from the U.S. Census Bureau, American Community Survey
          2020-2024 five-year estimates, Tables B03002, B01001, and B01001H.
        </p>

        <h3>2.10 Nebraska Panhandle vs. Nebraska: Comparative Overview</h3>
        <div className="table-wrapper">
          <table className="ceds-table">
            <thead>
              <tr>
                <th scope="col">Indicator</th>
                <th scope="col">Nebraska</th>
                <th scope="col">Panhandle</th>
                <th scope="col">Context</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map(([ind, ne, ph, ctx]) => (
                <tr key={ind}>
                  <td><strong>{ind}</strong></td>
                  <td>{ne}</td>
                  <td>{ph}</td>
                  <td>{ctx}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="table-note">
          *Panhandle figures reflect population-weighted averages across all
          eleven counties. Source: U.S. Census Bureau, ACS 5-Year Estimates. See
          Appendix D regarding small-county estimate reliability.
        </p>
      </div>
    </div>
  );
}
